import { Input, VStack } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { useEffect, useState } from "react"
import { useMutation } from "@apollo/client"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import ManageLocation from "@hydrologiq/shared/asset/location/ManageLocation"
import { useAppSelector } from "@hydrologiq/shared/state/hooks"
import { getLocations } from "@hydrologiq/shared/state/slices/locationsSlice"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "@hydrologiq/shared/utils/toast"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"

export interface CustomerSiteCreationFormProps {
  customerId: string
  onSuccess: (site: gqlTypes.SiteWithLocation) => void
  onCancel?: () => void
  onBack?: () => void
  mapsApiKey?: string
  existingSite?: gqlTypes.SiteWithLocation
}

type CustomerSiteFormData = {
  name: string
  location?: string
}

/**
 * Shared customer site creation form component.
 * Can be used across different sections without tight coupling to routing or section-specific state.
 */
const CustomerSiteCreationForm = ({
  customerId,
  onSuccess,
  onCancel,
  onBack,
  mapsApiKey,
  existingSite,
}: CustomerSiteCreationFormProps) => {
  const update = existingSite !== undefined

  const copy = useCopyValues({
    [COPY_KEYS.LABELS.NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.NAME],
    [COPY_KEYS.LABELS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.LABELS.SITE],
    [COPY_KEYS.MESSAGES.PLEASE_PROVIDE_NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.PLEASE_PROVIDE_NAME],
    [COPY_KEYS.MESSAGES.VALIDATION_ERROR]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.VALIDATION_ERROR],
  })

  const [createCustomerSite, { loading: createLoading }] = useMutation(gqlTypes.CreateCustomerSiteDocument, {
    // Update Apollo cache to add the new site to the customer
    update(cache, { data }) {
      if (!data?.createCustomerSite) return

      const newSite = data.createCustomerSite

      // Update the customer's sites array in cache
      cache.modify({
        id: cache.identify({ __typename: "Customer", id: customerId }),
        fields: {
          sites(existingSites = [], { toReference }) {
            const newSiteRef = toReference(newSite)

            // Check if site already exists
            if (existingSites.some((ref: any) => ref.__ref === newSiteRef?.__ref)) {
              return existingSites
            }

            // Add new site to the customer's sites
            return [...existingSites, newSiteRef]
          },
        },
      })
    },
  })
  const [updateCustomerSite, { loading: updateLoading }] = useMutation(gqlTypes.UpdateCustomerSiteDocument)
  const loading = createLoading || updateLoading
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<CustomerSiteFormData>({
    name: existingSite?.name ?? "",
    location: existingSite?.location?.id ?? undefined,
  })
  const locations = useAppSelector(getLocations)

  useEffect(() => {
    // Reset form data when customerId changes
    setFormData({
      name: existingSite?.name ?? "",
      location: existingSite?.location?.id ?? undefined,
    })
    setError(null)
  }, [customerId, existingSite])

  const handleSubmit = async () => {
    let loadingToastId: string | undefined
    try {
      setError(null)

      // Show loading toast
      loadingToastId = showLoadingToast(
        update ? `Updating ${copy[COPY_KEYS.LABELS.SITE]}` : `Creating ${copy[COPY_KEYS.LABELS.SITE]}`,
        update ? `Saving changes...` : `Adding ${copy[COPY_KEYS.LABELS.SITE]} to customer...`
      )

      if (!formData.name || !formData.name.trim()) {
        const errorMessage = `${copy[COPY_KEYS.MESSAGES.PLEASE_PROVIDE_NAME]} ${copy[COPY_KEYS.LABELS.SITE]}`
        setError(errorMessage)
        showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
        return false
      }

      let variables: gqlTypes.CreateCustomerSiteMutationVariables = {
        customerId: customerId,
        name: formData.name.trim(),
        location: formData.location ?? "",
      }

      const data = update
        ? await updateCustomerSite({ variables: { siteId: existingSite.id, ...variables } })
        : await createCustomerSite({ variables })

      if (data.data) {
        const siteData = update
          ? (data.data as gqlTypes.UpdateCustomerSiteMutation).updateCustomerSite
          : (data.data as gqlTypes.CreateCustomerSiteMutation).createCustomerSite

        showSuccessToast(
          update ? `${copy[COPY_KEYS.LABELS.SITE]} Updated` : `${copy[COPY_KEYS.LABELS.SITE]} Created`,
          `${copy[COPY_KEYS.LABELS.SITE]} "${formData.name}" has been successfully ${update ? "updated" : "created"}.`
        )

        setError(null)
        setFormData({
          name: "",
          location: undefined,
        })

        // Pass created site to parent
        onSuccess(siteData)

        return true
      } else {
        const errorMessage = `Failed to ${update ? "update" : "create"} ${
          copy[COPY_KEYS.LABELS.SITE]
        }. Please try again.`
        setError(errorMessage)
        showErrorToast(update ? "Update Failed" : "Creation Failed", errorMessage)
        return false
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : `An unexpected error occurred while ${update ? "updating" : "creating"} the ${copy[COPY_KEYS.LABELS.SITE]}`
      setError(errorMessage)
      showErrorToast(update ? "Update Failed" : "Creation Failed", errorMessage)
      return false
    } finally {
      // Always dismiss loading toast
      if (loadingToastId) {
        dismissToast(loadingToastId)
      }
    }
  }

  const handleCancel = () => {
    setError(null)
    setFormData({
      name: existingSite?.name ?? "",
      location: existingSite?.location?.id ?? undefined,
    })
    if (onCancel) onCancel()
  }

  const handleBack = () => {
    setError(null)
    setFormData({
      name: existingSite?.name ?? "",
      location: existingSite?.location?.id ?? undefined,
    })
    if (onBack) onBack()
  }

  return (
    <Form
      heading={update ? `Update ${copy[COPY_KEYS.LABELS.SITE]}` : `Create a ${copy[COPY_KEYS.LABELS.SITE]}`}
      helperText={`Please provide ${copy[COPY_KEYS.LABELS.SITE]} information below.`}
      actionType={update ? "update" : "create"}
      loading={loading}
      onSubmit={handleSubmit}
      onCancel={onCancel ? handleCancel : undefined}
      onBack={onBack ? handleBack : undefined}
    >
      <VStack gap={5}>
        {error && (
          <ErrorAlert
            title={
              update ? `${copy[COPY_KEYS.LABELS.SITE]} Update Error` : `${copy[COPY_KEYS.LABELS.SITE]} Creation Error`
            }
            message={error}
            onClose={() => setError(null)}
          />
        )}
        <Field label="Name" required>
          <Input
            name="name"
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }}
            value={formData.name}
            placeholder={`Enter ${copy[COPY_KEYS.LABELS.SITE]} name`}
          />
        </Field>
        <ManageLocation
          location={formData.location}
          locations={locations}
          onUpdate={(location) => {
            setFormData((prev) => ({
              ...prev,
              location: location,
            }))
          }}
          mapsApiKey={mapsApiKey}
          newLocationDefaults={{
            name: undefined,
            lat: 54.9753147,
            long: -2.269908,
          }}
        />
      </VStack>
    </Form>
  )
}

export default CustomerSiteCreationForm
